import {Modal, Button, Spinner, Alert} from 'react-bootstrap'
import Select from 'react-select'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useMutation} from '@apollo/client'
import {UPDATE_POLICIES_STATUS} from '../../../gql/mutations/policyMutation'
import {PolicyFilters} from '../policiesTypes'
import {PAYMENT_STATUS_OPTIONS, DOCUMENT_STATUS_OPTIONS} from '../../../helpers/consts'
import {PolicyStatus} from '../../../types/policies'
import {AppliedFiltersList, FilterConfig, DateRangeFilter} from './AppliedFiltersList'
import {parseId} from '../../../helpers'
import {
  buildPolicyReassignFilterVariables,
  getActiveFilterSummary,
} from './ModalReassignPolicies'

interface SelectedPolicy {
  id: string
  name: string
  status: string
  user: string
  agent: string
}

interface ModalUpdatePoliciesProps {
  isOpen: boolean
  onClose: () => void
  updateListPolicies: () => void
  filters: PolicyFilters
  tabFilters: string
  selectedYears: Array<string | number>
  searchByMembers: boolean
  totalItems: number
  policyStatuses: PolicyStatus[]
  policyTypeOptions: PolicyTypeOption[]
  filtersConfig: FilterConfig[]
  dateRangeFilters: DateRangeFilter[]
  formatDate: (date: string) => string
  selectedPolicies: SelectedPolicy[]
}

interface IResUpdatePoliciesStatus {
  updatePoliciesStatus: {
    success: boolean
    message: string
    errors?: {field: string; message: string}[]
  }
}

interface StatusOption {
  value: string
  label: string
}

interface PolicyTypeOption {
  value: number
  label: string
}

type Step = 'form' | 'confirm1' | 'confirm2'

export const ModalUpdatePolicies = ({
  isOpen,
  onClose,
  updateListPolicies,
  filters,
  tabFilters,
  selectedYears,
  searchByMembers,
  totalItems,
  policyStatuses,
  policyTypeOptions,
  filtersConfig,
  dateRangeFilters,
  formatDate,
  selectedPolicies,
}: ModalUpdatePoliciesProps) => {
  const [step, setStep] = useState<Step>('form')
  const [newStatus, setNewStatus] = useState<StatusOption | null>(null)
  const [newPaymentStatus, setNewPaymentStatus] = useState<StatusOption | null>(null)
  const [newDocumentStatus, setNewDocumentStatus] = useState<StatusOption | null>(null)
  const [newPolicyType, setNewPolicyType] = useState<PolicyTypeOption | null>(null)

  const [gqlCall, {data, loading, error}] = useMutation<IResUpdatePoliciesStatus>(
    UPDATE_POLICIES_STATUS
  )

  const bySelected = selectedPolicies.length > 0

  const activeFilterSummary = useMemo(
    () => getActiveFilterSummary(filters, tabFilters, selectedYears, searchByMembers),
    [filters, searchByMembers, selectedYears, tabFilters]
  )
  const hasFilterSource = activeFilterSummary.length > 0

  const hasSource = bySelected || hasFilterSource
  const disabledUpdate =
    !hasSource || (!newStatus && !newPaymentStatus && !newDocumentStatus && !newPolicyType)

  const affectedCount = bySelected ? selectedPolicies.length : totalItems
  const countLabel = `${affectedCount.toLocaleString()} ${affectedCount === 1 ? 'policy' : 'policies'}`

  const policyStatusOptions = useMemo<StatusOption[]>(() => {
    return policyStatuses.map((s) => ({value: s.code, label: s.title}))
  }, [policyStatuses])

  const modalClose = useCallback(() => {
    setStep('form')
    setNewStatus(null)
    setNewPaymentStatus(null)
    setNewDocumentStatus(null)
    setNewPolicyType(null)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) setStep('form')
  }, [isOpen])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.updatePoliciesStatus?.success) {
      toast.success(data.updatePoliciesStatus.message)
      updateListPolicies()
      modalClose()
      return
    }
    const message = data.updatePoliciesStatus?.errors?.[0]?.message
    if (message) toast.error(`Error: ${message}`)
  }, [data, modalClose, updateListPolicies])

  const handleConfirm1 = () => {
    if (disabledUpdate) return
    setStep('confirm1')
  }

  const handleConfirm2 = () => setStep('confirm2')

  const handleExecute = () => {
    const base: Record<string, unknown> = {
      newStatus: newStatus?.value ?? null,
      newPaymentStatus: newPaymentStatus?.value ?? null,
      newDocumentStatus: newDocumentStatus?.value ?? null,
      newPolicyType: newPolicyType?.value ?? null,
    }

    if (bySelected) {
      base.policyIds = selectedPolicies
        .map((p) => Number(parseId(p.id)))
        .filter((id) => Number.isFinite(id) && id > 0)
    } else {
      Object.assign(
        base,
        buildPolicyReassignFilterVariables(filters, tabFilters, selectedYears, searchByMembers)
      )
    }

    gqlCall({variables: base})
  }

  const selectedChanges: string[] = []
  if (newStatus) selectedChanges.push(`Policy status → ${newStatus.label}`)
  if (newDocumentStatus) selectedChanges.push(`Document status → ${newDocumentStatus.label}`)
  if (newPaymentStatus) selectedChanges.push(`Payment status → ${newPaymentStatus.label}`)
  if (newPolicyType) selectedChanges.push(`Policy type → ${newPolicyType.label}`)

  return (
    <Modal show={isOpen} onHide={modalClose} size='lg' className='modal-update-policies'>
      <Modal.Header closeButton>
        <Modal.Title>
          <span className='fw-bold'>Update policies</span>
        </Modal.Title>
      </Modal.Header>

      {step === 'form' && (
        <>
          <Modal.Body>
            <div className='d-flex flex-column gap-4'>
              <div>
                <h2 className='fs-3 mb-3'>Affected policies</h2>
                <div className='rounded border p-3 bg-light'>
                  {bySelected ? (
                    <div className='d-flex flex-column gap-1'>
                      <div className='fw-semibold mb-1'>
                        {selectedPolicies.length} {selectedPolicies.length === 1 ? 'policy' : 'policies'} selected:
                      </div>
                      <div className='d-flex flex-wrap gap-2'>
                        {selectedPolicies.map((p) => (
                          <span key={p.id} className='badge bg-primary-subtle text-dark'>
                            {p.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : hasFilterSource ? (
                    <div className='d-flex flex-column gap-2'>
                      {(tabFilters === 'Important' || tabFilters === 'Archived' || tabFilters === 'Reminders' || selectedYears.length > 0 || filters.policyName) && (
                        <div className='d-flex flex-wrap gap-2'>
                          {tabFilters === 'Important' && <span className='badge bg-secondary-subtle text-dark'>Important</span>}
                          {tabFilters === 'Archived' && <span className='badge bg-secondary-subtle text-dark'>Archived</span>}
                          {tabFilters === 'Reminders' && <span className='badge bg-secondary-subtle text-dark'>Has reminders</span>}
                          {selectedYears.length > 0 && <span className='badge bg-secondary-subtle text-dark'>Years: {selectedYears.join(', ')}</span>}
                          {filters.policyName && <span className='badge bg-secondary-subtle text-dark'>{searchByMembers ? 'Member' : 'Policy'}: {filters.policyName}</span>}
                        </div>
                      )}
                      <AppliedFiltersList
                        filters={filters}
                        config={filtersConfig}
                        dateRangeFilters={dateRangeFilters}
                        formatDate={formatDate}
                      />
                    </div>
                  ) : (
                    <span className='text-muted'>
                      No policies selected and no filters applied. Select policies or apply filters first.
                    </span>
                  )}
                </div>
                {hasSource && (
                  <div className='mt-2 text-muted fs-7'>
                    <strong>{countLabel}</strong> will be affected.
                  </div>
                )}
              </div>

              <div>
                <h2 className='fs-3 mb-3'>New values</h2>
                <div className='d-flex flex-column gap-3'>
                  <div>
                    <div className='mb-2 fw-semibold'>Policy status</div>
                    <Select<StatusOption, false>
                      value={newStatus}
                      options={policyStatusOptions}
                      onChange={(selected) => setNewStatus(selected)}
                      placeholder='Keep unchanged'
                      isClearable
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>Document status</div>
                    <Select<StatusOption, false>
                      value={newDocumentStatus}
                      options={DOCUMENT_STATUS_OPTIONS as StatusOption[]}
                      onChange={(selected) => setNewDocumentStatus(selected)}
                      placeholder='Keep unchanged'
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>Payment status</div>
                    <Select<StatusOption, false>
                      value={newPaymentStatus}
                      options={PAYMENT_STATUS_OPTIONS as StatusOption[]}
                      onChange={(selected) => setNewPaymentStatus(selected)}
                      placeholder='Keep unchanged'
                      isClearable
                      isSearchable={false}
                    />
                  </div>
                  <div>
                    <div className='mb-2 fw-semibold'>Policy type</div>
                    <Select<PolicyTypeOption, false>
                      value={newPolicyType}
                      options={policyTypeOptions}
                      onChange={(selected) => setNewPolicyType(selected)}
                      placeholder='Keep unchanged'
                      isClearable
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={modalClose} variant='light'>
              Cancel
            </Button>
            <Button onClick={handleConfirm1} variant='primary' disabled={disabledUpdate}>
              <i className='bi bi-pencil-square' /> Update policies
            </Button>
          </Modal.Footer>
        </>
      )}

      {(step === 'confirm1' || step === 'confirm2') && (
        <>
          <Modal.Body>
            <Alert variant={step === 'confirm1' ? 'warning' : 'danger'} className='mb-3'>
              <Alert.Heading>
                <i className={`bi ${step === 'confirm1' ? 'bi-exclamation-triangle-fill' : 'bi-exclamation-octagon-fill'} me-2`} />
                {step === 'confirm1' ? 'Are you sure?' : 'Final confirmation'}
              </Alert.Heading>
              <p className='mb-0'>
                {step === 'confirm1'
                  ? <>You are about to update <strong>{countLabel}</strong>. This action <strong>cannot be undone</strong>.</>
                  : <>Are you <strong>absolutely sure</strong>? You are about to permanently update <strong>{countLabel}</strong>. There is no way to reverse this automatically.</>
                }
              </p>
            </Alert>

            <div className='mb-3'>
              <div className='fw-semibold mb-2'>Changes to apply:</div>
              <ul className='mb-0'>
                {selectedChanges.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>

            <div className='mb-0'>
              <div className='fw-semibold mb-2'>{bySelected ? 'Selected policies:' : 'Active filters:'}</div>
              <div className='rounded border p-2 bg-light'>
                {bySelected ? (
                  <div className='d-flex flex-wrap gap-2'>
                    {selectedPolicies.map((p) => (
                      <span key={p.id} className='badge bg-primary-subtle text-dark'>
                        {p.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className='d-flex flex-column gap-2'>
                    {(tabFilters === 'Important' || tabFilters === 'Archived' || tabFilters === 'Reminders' || selectedYears.length > 0 || filters.policyName) && (
                      <div className='d-flex flex-wrap gap-2'>
                        {tabFilters === 'Important' && <span className='badge bg-secondary-subtle text-dark'>Important</span>}
                        {tabFilters === 'Archived' && <span className='badge bg-secondary-subtle text-dark'>Archived</span>}
                        {tabFilters === 'Reminders' && <span className='badge bg-secondary-subtle text-dark'>Has reminders</span>}
                        {selectedYears.length > 0 && <span className='badge bg-secondary-subtle text-dark'>Years: {selectedYears.join(', ')}</span>}
                        {filters.policyName && <span className='badge bg-secondary-subtle text-dark'>{searchByMembers ? 'Member' : 'Policy'}: {filters.policyName}</span>}
                      </div>
                    )}
                    <AppliedFiltersList
                      filters={filters}
                      config={filtersConfig}
                      dateRangeFilters={dateRangeFilters}
                      formatDate={formatDate}
                    />
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setStep(step === 'confirm1' ? 'form' : 'confirm1')} variant='light'>
              {step === 'confirm1' ? 'Cancel' : 'Go back'}
            </Button>
            {step === 'confirm1' ? (
              <Button onClick={handleConfirm2} variant='warning'>
                Yes, continue
              </Button>
            ) : (
              <Button onClick={handleExecute} variant='danger' disabled={loading}>
                {loading && <Spinner animation='border' className='me-2' size='sm' />}
                <i className='bi bi-check-circle' /> Yes, update {countLabel}
              </Button>
            )}
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}
